
import React, { useState } from 'react';
import { Mail, Lock, User, ShieldCheck, ArrowRight } from 'lucide-react';
import { AuthState } from '../../types';

interface AuthViewProps {
  type: 'login' | 'register';
  setAuthState: (state: AuthState) => void;
}

export const AuthView: React.FC<AuthViewProps> = ({ type, setAuthState }) => {
  const [email, setEmail] = useState('admin@minerva.com');
  const [password, setPassword] = useState('password123');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAuthState('pricing');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      <div className="flex-1 bg-slate-900 flex flex-col items-center justify-center p-12 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
          <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-blue-500 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-indigo-500 rounded-full blur-[120px]"></div>
        </div>
        
        <div className="relative z-10 max-w-md w-full">
          <div className="flex items-center space-x-4 mb-8">
            <img src="assets/logo.png" alt="Minerva Logo" className="w-10 h-10 object-contain shadow-lg shadow-blue-500/40" />
            <span className="text-2xl font-black tracking-tighter uppercase italic">Minerva RegTech</span>
          </div>
          <h1 className="text-4xl font-black mb-6 leading-tight">API-Driven Compliance Intelligence.</h1>
          <p className="text-slate-400 text-lg mb-8 leading-relaxed">
            Verify contacts, monitor transactions, and track ESG risk with our unified intelligence portal.
          </p>
          
          <div className="space-y-4">
            <Feature icon={<ShieldCheck className="text-emerald-400" />} text="Real-time AML & Transaction Monitoring" />
            <Feature icon={<ShieldCheck className="text-emerald-400" />} text="ESG Scoring & Reputation Tracking" />
            <Feature icon={<ShieldCheck className="text-emerald-400" />} text="Unified API for KYC/LEI Validation" />
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 md:p-24 bg-white">
        <div className="max-w-md w-full animate-in fade-in slide-in-from-right duration-500">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-black text-slate-900 mb-2">
              {type === 'login' ? 'Welcome Back' : 'Get Started'}
            </h2>
            <p className="text-slate-500 font-medium">
              {type === 'login' ? 'Enter your credentials to access your dashboard' : 'Join thousands of compliant financial institutions'}
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {type === 'register' && (
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    type="text" 
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all" 
                    placeholder="Julian Casablancas" 
                    defaultValue="Julian Casablancas"
                    required 
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700">Business Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all" 
                  placeholder="julian@company.com" 
                  required 
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-bold text-slate-700">Password</label>
                {type === 'login' && <button type="button" className="text-xs font-bold text-blue-600 hover:underline">Forgot password?</button>}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all" 
                  placeholder="••••••••" 
                  required 
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold flex items-center justify-center space-x-2 hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 active:scale-95"
            >
              <span>{type === 'login' ? 'Sign In' : 'Create Account'}</span>
              <ArrowRight size={18} />
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-slate-500 font-medium">
            {type === 'login' ? (
              <>Don't have an account? <button onClick={() => setAuthState('register')} className="text-blue-600 font-bold hover:underline">Sign up for free</button></>
            ) : (
              <>Already have an account? <button onClick={() => setAuthState('login')} className="text-blue-600 font-bold hover:underline">Log in</button></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Feature: React.FC<{ icon: React.ReactNode, text: string }> = ({ icon, text }) => (
  <div className="flex items-center space-x-3 text-slate-200 font-medium text-sm">
    <div className="p-1.5 bg-slate-800 rounded-lg">{icon}</div>
    <span>{text}</span>
  </div>
);
